/**
 * Review suggestions (ADR-0102 addendum 2) as action-log entries.
 *
 * In review every text change is a SUGGESTION: an ordinary content entry
 * with `proposal: true`. The replay never applies it to the clean document;
 * the proposal overlay (`replayLog(…, { proposals: 'overlay', track: true })`,
 * `previewWithProposals`) shows it red/green. Its lifecycle is the log too:
 *
 *   proposed ──acceptSuggestion──▶ accepted (an ordinary adaptation from then on)
 *      │  ╲───rejectSuggestion──▶ rejected (kept in history, never applied)
 *      ▼
 *    stale   (its anchor no longer matches, or a later adaptation of the
 *             same node superseded it) — can only be rejected
 *
 * `proposed` and `stale` are OPEN. A suggestion anchored inside another
 * suggestion (`{ fromSeq }`) is rejected with it and cannot be accepted
 * before it. Decisions are final: they cannot be undone, and only the
 * first decision of a suggestion counts.
 *
 * Who may suggest, accept or reject is the host's policy.
 */
import { formatLogAddress, formatNodeAddress } from './address.js';
import { applyRebase, dependsOn, foldLog, isContentEntry, isProposal } from './fold.js';
import { appendAction, replayLog } from './log.js';
export class SuggestionError extends Error {
    reason;
    targetSeq;
    constructor(reason, targetSeq) {
        super(`suggestion #${targetSeq}: ${reason}`);
        this.reason = reason;
        this.targetSeq = targetSeq;
    }
}
const OPEN = new Set(['proposed', 'stale']);
/** Whether a report row is an open suggestion (`proposed` or `stale`). */
export const isOpenSuggestion = (row) => OPEN.has(row.status);
/** Open suggestions in a replay report — what blocks signing until decided. */
export const openSuggestionCount = (report) => report.filter(isOpenSuggestion).length;
/**
 * Seqs of the open suggestions (not accepted, rejected or undone) — from
 * the log alone, no replay. Stale ones are open too: they still need a
 * rejection. What a host's signing gate counts.
 */
export function openSuggestionSeqs(log) {
    const folded = foldLog(log);
    return [...folded.suggestions]
        .filter(([seq, s]) => s.state === 'open' && !folded.undone.has(seq) && !folded.malformed.has(seq))
        .map(([seq]) => seq)
        .sort((a, b) => a - b);
}
const isInsert = (e) => e.op === 'insertParagraph' || e.op === 'insertClause' || e.op === 'insertRow';
/** Every suggestion of the log with its state (from `report`, the replay of `log`), in seq order. */
export function listSuggestions(log, report) {
    const folded = foldLog(log);
    const rows = new Map(report.map((r) => [r.seq, r]));
    const bySeq = new Map(log.entries.map((e) => [e.seq, e]));
    const groupOf = new Map();
    const out = [];
    for (const e of [...log.entries].sort((a, b) => a.seq - b.seq)) {
        if (!isContentEntry(e) || folded.malformed.has(e.seq))
            continue;
        const { anchor } = applyRebase(e, folded.rebases.get(e.seq));
        const dep = dependsOn(anchor);
        const groupId = (dep != null ? groupOf.get(dep) : undefined) ?? (dep == null && isInsert(e) ? `insert:${e.seq}` : `node:${formatLogAddress(anchor)}`);
        groupOf.set(e.seq, groupId);
        if (!isProposal(e))
            continue;
        const row = rows.get(e.seq);
        const fold = folded.suggestions.get(e.seq);
        const state = row?.suggestion ?? (fold?.state === 'accepted' ? 'accepted' : fold?.state === 'rejected' ? 'rejected' : 'proposed');
        const undone = row?.status === 'undone' && state !== 'accepted';
        const decidedBy = row?.decidedBy ?? fold?.decidedBy;
        const decision = decidedBy != null ? bySeq.get(decidedBy) : undefined;
        const parent = dep != null && folded.suggestions.has(dep) ? dep : undefined;
        out.push({
            seq: e.seq,
            op: e.op,
            author: e.author,
            at: e.at,
            state: undone ? 'rejected' : state,
            open: !undone && OPEN.has(row?.status ?? ''),
            address: groupId.startsWith('insert:') && dep == null ? formatNodeAddress({ fromPatch: String(e.seq) }) : (row?.address ?? formatLogAddress(anchor)),
            groupId,
            ...(row?.status === 'stale' && row.reason ? { reason: row.reason } : {}),
            ...(row?.status === 'stale' && row.supersededBy != null ? { supersededBy: row.supersededBy } : {}),
            ...(decidedBy != null ? { decidedBy } : {}),
            ...(decision ? { decidedByAuthor: decision.author } : {}),
            ...(parent != null ? { parentSeq: parent } : {}),
        });
    }
    return out;
}
/**
 * Why accepting / rejecting suggestion `targetSeq` is refused, or `null`.
 * Pass the replay report of `log` — staleness is the replay's verdict
 * (without it, accept is refused as `stale` to be safe).
 */
export function suggestionRefusal(log, targetSeq, decision, report) {
    const folded = foldLog(log);
    const target = log.entries.find((e) => e.seq === targetSeq);
    if (!target || folded.malformed.has(targetSeq) || !isProposal(target))
        return 'not_a_suggestion';
    const s = folded.suggestions.get(targetSeq);
    if (!s || s.state !== 'open' || folded.undone.has(targetSeq))
        return 'already_decided';
    if (decision === 'reject')
        return null;
    const { anchor } = applyRebase(target, undefined);
    const dep = dependsOn(anchor);
    if (dep != null && folded.suggestions.get(dep)?.state === 'open')
        return 'depends_on_open_suggestion';
    const row = report?.find((r) => r.seq === targetSeq);
    return row?.status === 'proposed' ? null : 'stale';
}
function decide(log, targetSeq, decision, meta, report) {
    const refusal = suggestionRefusal(log, targetSeq, decision, report);
    if (refusal)
        throw new SuggestionError(refusal, targetSeq);
    return appendAction(log, { op: decision === 'accept' ? 'acceptSuggestion' : 'rejectSuggestion', ...meta, payload: { targetSeq } });
}
/** Appends `acceptSuggestion`. Throws `SuggestionError` when refused (pass the replay report of `log`). */
export const acceptSuggestion = (log, targetSeq, meta, report) => decide(log, targetSeq, 'accept', meta, report);
/** Appends `rejectSuggestion`. Throws `SuggestionError` when refused. */
export const rejectSuggestion = (log, targetSeq, meta) => decide(log, targetSeq, 'reject', meta);
/** A content entry as a suggestion (what a host forces in review). Other entries are returned unchanged. */
export function asProposal(entry) {
    return isContentEntry(entry) ? { ...entry, proposal: true } : entry;
}
/**
 * The review view: the clean replay with every open suggestion applied on
 * top, tracked (`<ins>` / `<del>` with `data-layer` / `data-suggestion`).
 * Display only — never issue it.
 */
export const previewWithProposals = (compiled, log, options = {}) => replayLog(compiled, log, { track: true, ...options, proposals: 'overlay' });
//# sourceMappingURL=suggestions.js.map